import { Injectable } from '@angular/core';
import {
    CancelLoadAssetsCallbacks,
    IRequestCallback,
    IRequestCallbackFunction,
    IRequestSubjectEvent,
    RequestService
} from '@df/api/request/request.service';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Events } from '@df/core/service/event';
import { Status } from '@df/core/status';
import { Stripe } from '@df/module-stripe/stripe.namespace';
import { Nexus } from '@tomandco/nexus-api-types';
import findIndex from 'lodash-es/findIndex';

@Injectable({ providedIn: 'root' })
export class StripeService {
    private _isPaymentRequestButtonAvailable = false;

    set isPaymentRequestButtonAvailable(value: boolean) {
        const was = this.isPaymentRequestButtonAvailable;
        this._isPaymentRequestButtonAvailable = value;
        if (was !== value) {
            this.events.broadcast(Checkout.EVENTS.RefreshPaymentMethods);
        }
    }

    get isPaymentRequestButtonAvailable(): boolean {
        return this._isPaymentRequestButtonAvailable;
    }

    private _stripeInstance?: Stripe.Instance;

    constructor(
        private requestService: RequestService,
        private events: Events,
        private app: App
    ) {
        this.events.on(Checkout.EVENTS.Destroy).subscribe(() => {
            this._stripeInstance = undefined;
            this.isPaymentRequestButtonAvailable = false;
        });
        this.events.on(Checkout.EVENTS.SetPaymentMethodsBefore).subscribe((methods: Checkout.IPaymentMethod[]) => {
            if (!this.isPaymentRequestButtonAvailable && methods?.length) {
                const index = findIndex(methods, method => {
                    const code = method.code;
                    if (typeof code === 'string') {
                        return code === Stripe.PaymentMethod.StripePaymentsPrapi;
                    }
                    return false;
                });
                if (index > -1) {
                    methods.splice(index, 1);
                }
            }
        });
    }

    /**
     * Loads stripe 3rd party script, used for express payment methods used outside regular checkout.
     * @param {ICallableFunction} onSuccess
     * @param {ICallableFunction} onError
     * @returns {void | CancelLoadAssetsCallbacks}
     */
    loadScript(onLoad?: IRequestCallbackFunction, onError?: IRequestCallbackFunction): void | CancelLoadAssetsCallbacks {
        return this.requestService.loadScript(Stripe.Scripts.JS, { onLoad, onError });
    }

    /**
     * Creates stripe object
     * @param {string}                  publishableKey
     * @param {Stripe.InstanceOptions}  options
     * @returns {Stripe.Instance | undefined}
     */
    getStripe(publishableKey: string, options?: Stripe.InstanceOptions): Stripe.Instance | undefined {
        if (!this._stripeInstance && typeof window !== 'undefined' && window.Stripe) {
            this._stripeInstance = window.Stripe(publishableKey, options);
        }
        return this._stripeInstance;
    }

    /**
     * Used to get config for express payment, where we dont have it in checkout data.
     * @returns {Stripe.IExpressConfig | undefined}
     */
    getPaymentRequestButtonConfig(): Stripe.IExpressConfig | undefined {
        return this.app.data!.stripe;
    }

    //////////////////////////////////////////
    /////
    /////   Client secret
    /////
    //////////////////////////////////////////

    /**
     * Load client secret required for stripe elements
     * @param {Status}                                      status
     * @param {Stripe.IGetClientSecretData}                 data
     * @param {IRequestCallback}                            onSuccess
     * @param {IRequestCallback}                            onError
     * @returns {Promise<Stripe.IGetClientSecretResponse>}
     */
    getClientSecret(
        status: Status | undefined,
        data: Stripe.IGetClientSecretData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<Stripe.IGetClientSecretResponse> {
        return this.requestService.send<Stripe.IGetClientSecretResponse>(
            status,
            { path: Stripe.Endpoints.GetSetupIntent, data },
            onSuccess,
            onError
        );
    }

    //////////////////////////////////////////
    /////
    /////   Prapi params
    /////
    //////////////////////////////////////////

    /**
     * Load payments request params for payment request button element
     * @param {Status}                                      status
     * @param {Stripe.IGetClientSecretData}                 data
     * @param {IRequestCallback}                            onSuccess
     * @param {IRequestCallback}                            onError
     * @returns {Promise<Stripe.IPaymentRequestOptions>}
     */
    getPaymentRequestOptions(
        status: Status | undefined,
        data: Stripe.IGetPaymentsRequestAPIParamsData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<Stripe.IPaymentRequestOptions> {
        return this.requestService.send(status, { path: Stripe.Endpoints.GetPaymentsRequestAPIParams, data }, onSuccess, onError);
    }

    //////////////////////////////////////////
    /////
    /////   Authentication
    /////
    //////////////////////////////////////////

    /**
     * Process authentication if required
     * @param {Stripe.Instance}             stripe
     * @param {Status}                      status
     * @param {Status}                      submitStatus
     * @param {IRequestCallbackFunction}    onSuccess
     * @param {IRequestCallbackFunction}    onError
     */
    checkAuthentication(
        stripe: Stripe.Instance,
        status: Status,
        submitStatus: Status,
        onSuccess?: IRequestCallbackFunction,
        onError?: IRequestCallbackFunction
    ): void {
        const requestSubscription = this.requestService.requestSubject.subscribe((request: IRequestSubjectEvent) => {
            if (request.path === Nexus.Api.Checkout.Submit.path) {
                if (['error', 'success', 'cancelled'].includes(request.status)) {
                    requestSubscription.unsubscribe();
                }
                if (request.status === 'error') {
                    const errorMessage = request.error;
                    if (this.isAuthenticationRequired(errorMessage) && submitStatus.promise?.cancel) {
                        submitStatus.promise.cancel();
                        const paymentIntents = this.getPaymentIntentsFromErrorMessage(errorMessage!);
                        if (paymentIntents?.length) {
                            status.busy = true;
                            this.processNextAuthentication(paymentIntents, stripe).then(onSuccess).catch(onError);
                        } else {
                            if (onError) {
                                onError(Stripe.AUTH_ERROR_MESSAGE);
                            }
                        }
                    }
                }
            }
        });
    }

    /**
     * Returns payment intents from error message
     * @param {string} errorMessage
     * @returns {string[] | undefined}
     */
    private getPaymentIntentsFromErrorMessage(errorMessage: string): string[] {
        return errorMessage.substring(Stripe.AUTH_ERROR_START.length).split(',');
    }

    /**
     * Checks if authentication is required
     * @param {string} errorMessage
     * @returns {boolean}
     */
    private isAuthenticationRequired(errorMessage?: string): boolean {
        if (errorMessage && typeof errorMessage === 'string') {
            return errorMessage.indexOf(Stripe.AUTH_ERROR_START) >= 0;
        }
        return false;
    }

    /**
     * Processes authentications
     * @param {string[]}        paymentIntents
     * @param {Stripe.Instance} stripe
     * @returns {Promise<void>}
     */
    private processNextAuthentication(paymentIntents: string[], stripe: Stripe.Instance): Promise<void> {
        return new Promise((resolve, reject) => {
            const process = () => {
                if (paymentIntents?.length > 0) {
                    const paymentIntent = paymentIntents.pop();
                    this.authenticateCustomer(paymentIntent || '', stripe)
                        .then(() => {
                            process();
                        })
                        .catch(reject);
                } else {
                    resolve();
                }
            };
            process();
        });
    }

    /**
     * Authenticate customer
     * @param {string}          paymentIntent
     * @param {Stripe.Instance} stripe
     * @returns {Promise<void>}
     */
    private authenticateCustomer(paymentIntent: string, stripe: Stripe.Instance): Promise<void> {
        return new Promise((resolve, reject) => {
            stripe
                .retrievePaymentIntent(paymentIntent)
                .then((response: Stripe.IRetrievePaymentIntentResponse) => {
                    if (response?.error) {
                        reject(response.error?.message);
                    } else {
                        const status = response.paymentIntent?.status;
                        if (status === 'requires_action' || status === 'requires_source_action') {
                            const finish = (response: Stripe.IRetrievePaymentIntentResponse) => {
                                if (response?.error) {
                                    reject(response.error.message);
                                } else {
                                    resolve();
                                }
                            };
                            if (response.paymentIntent.confirmation_method === 'manual') {
                                stripe.handleCardAction(paymentIntent).then(finish).catch(reject);
                            } else {
                                stripe.handleCardPayment(paymentIntent).then(finish).catch(reject);
                            }
                        } else {
                            resolve();
                        }
                    }
                })
                .catch(reject);
        });
    }

    //////////////////////////////////////////
    /////
    /////   Stripe Express
    /////
    //////////////////////////////////////////

    /**
     * returns shipping otpions for the selected address.
     * Please note tha dddress we see in FE data always only includes country code and partial postcode,
     * the remaining data is usually empty, and this is normal.
     */
    expressEstimateCart(
        status: Status,
        data: Stripe.IExpressEstimateCartData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<Stripe.IExpressEstimateCartResult> {
        return this.requestService.send(status, { path: Stripe.Endpoints.EstimateCart, data }, onSuccess, onError);
    }

    /**
     * saves shipping method selected within external payment provider widget
     */
    expressApplyShipping(
        status: Status,
        data: Stripe.IExpressApplyShippingData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<Stripe.IExpressApplyShippingResult> {
        return this.requestService.send(status, { path: Stripe.Endpoints.ApplyShipping, data }, onSuccess, onError);
    }

    /**
     * Places the actual Magento order once payment is confirmed
     */
    expressPlaceOrder(
        status: Status,
        data: Stripe.IExpressPlaceOrderData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<Checkout.ISubmissionResponse> {
        return this.requestService.send(status, { path: Stripe.Endpoints.PlaceOrder, data }, onSuccess, onError);
    }
}
